import { Capacitor } from '@capacitor/core';
import { apiEndpoints } from './api';

const PENDING_TOKEN_KEY = 'fleet_pending_fcm_token';
let listenersReady = false;

export async function flushFCMToken() {
  const token = localStorage.getItem(PENDING_TOKEN_KEY);
  if (!token) return;
  try {
    await apiEndpoints.deviceTokens.save({ token, platform: 'android' });
    localStorage.removeItem(PENDING_TOKEN_KEY);
  } catch { /* will retry next call */ }
}

export async function ensureTokenRegistered() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const { receive } = await PushNotifications.checkPermissions();
    if (receive !== 'granted') return;
    await PushNotifications.register();
  } catch { /* not available */ }
}

export async function initPushNotifications() {
  if (!Capacitor.isNativePlatform()) return;

  let PushNotifications;
  try {
    ({ PushNotifications } = await import('@capacitor/push-notifications'));
  } catch {
    return;
  }

  // Set up ALL listeners FIRST before calling register()
  // (prevents race where token arrives before listener is wired)
  if (!listenersReady) {
    listenersReady = true;

    PushNotifications.addListener('registration', async ({ value: token }) => {
      localStorage.setItem(PENDING_TOKEN_KEY, token);
      try {
        await apiEndpoints.deviceTokens.save({ token, platform: Capacitor.getPlatform() });
        localStorage.removeItem(PENDING_TOKEN_KEY);
      } catch { /* no JWT yet — flushFCMToken() will finish after login */ }
    });

    PushNotifications.addListener('registrationError', (err) => {
      const msg = typeof err === 'string' ? err : JSON.stringify(err);
      console.warn('[Push] Registration error:', msg);
      apiEndpoints.health().catch(() => {});
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      window.dispatchEvent(new CustomEvent('fleet:push', { detail: notification }));
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification?.data || {};
      if (data.type === 'alert' || data.type === 'reminder') {
        if (window.location.pathname !== '/alerts') window.location.assign('/alerts');
      }
    });
  }

  // Create notification channel (Android 8+)
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.createChannel({
      id: 'fleet_alerts',
      name: 'Fleet Alerts',
      description: 'Reminders and document expiry alerts',
      importance: 5,
      visibility: 1,
      lights: true,
      vibration: true,
      sound: 'default',
    });
  } catch { /* plugin may not be available */ }

  // Request permission then register
  try {
    const { receive } = await PushNotifications.checkPermissions();
    let granted = receive === 'granted';
    if (!granted) {
      const result = await PushNotifications.requestPermissions();
      granted = result.receive === 'granted';
    }
    if (granted) await PushNotifications.register();
  } catch (err) {
    console.warn('[Push] Init failed:', err);
  }
}
